import { useState } from "react";
import { addBreadcrumb, captureError } from "@/lib/observability";
import PictureImg from "@/components/ui/PictureImg";

// Dedupe: only report each missing asset once per page load to avoid noise.
const reportedFailures = new Set<string>();

/**
 * Renders a softly-masked atmospheric backdrop image with a graceful fallback
 * and an optional low-quality image placeholder (LQIP) that fades into the
 * full-resolution asset. If the image fails to load (404, network error,
 * blocked), we swap to a premium midnight-blue → cream radial gradient so the
 * section never collapses to a flat slab. Decorative only — never announced
 * to AT.
 */
interface AtmosphericBackdropProps {
  src?: string;
  /** Public-path base for AVIF/WebP variants generated by optimize-images.mjs. */
  optimizedBase?: string;
  /** Tailwind classes for size + position + opacity + mask. */
  className?: string;
  /** Where the fallback gradient should center. */
  fallbackOrigin?:
    | "center"
    | "top left"
    | "top right"
    | "bottom left"
    | "bottom right";
  width?: number;
  height?: number;
  /**
   * Optional tiny base64 data URI (or any URL) used as a blurred placeholder
   * while the full image loads. Recommended size: 16–32px wide WebP/JPEG.
   * If omitted, a soft tinted gradient matching `fallbackOrigin` is used.
   */
  lqip?: string;
  /** Disable the fade-in transition (defaults to enabled). */
  noFade?: boolean;
}

const originToCss: Record<NonNullable<AtmosphericBackdropProps["fallbackOrigin"]>, string> = {
  center: "center",
  "top left": "top left",
  "top right": "top right",
  "bottom left": "bottom left",
  "bottom right": "bottom right",
};

const AtmosphericBackdrop = ({
  src,
  optimizedBase,
  className = "",
  fallbackOrigin = "center",
  width = 1920,
  height = 1080,
  lqip,
  noFade = false,
}: AtmosphericBackdropProps) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fallbackBg = `radial-gradient(ellipse at ${originToCss[fallbackOrigin]}, hsl(var(--primary) / 0.12), transparent 65%)`;

  if (!src || failed) {
    return (
      <div
        aria-hidden="true"
        className={className}
        style={{ backgroundImage: fallbackBg, backgroundColor: "transparent" }}
      />
    );
  }

  // Placeholder layer: either the provided LQIP (blurred) or the soft gradient.
  const placeholderStyle: React.CSSProperties = lqip
    ? {
        backgroundImage: `url("${lqip}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        filter: "blur(20px)",
        transform: "scale(1.05)", // hide blur edge bleed
      }
    : { backgroundImage: fallbackBg };

  const fadeMs = noFade ? 0 : 700;

  return (
    <>
      {/* Placeholder — fades OUT once the real image is loaded.
          We only override opacity when fading; otherwise we leave it to the
          className (e.g. opacity-[0.09]) so the placeholder matches the final
          image's intended dimness. */}
      <div
        aria-hidden="true"
        className={className}
        style={{
          ...placeholderStyle,
          ...(loaded && !noFade ? { opacity: 0 } : {}),
          transition: `opacity ${fadeMs}ms ease-out`,
        }}
      />
      {/* Real image — fades IN over the placeholder */}
      <PictureImg
        src={src}
        optimizedBase={optimizedBase}
        alt=""
        aria-hidden="true"
        loading="lazy"
        sizes="(min-width: 1280px) 820px, 60vw"
        decoding="async"
        width={width}
        height={height}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setFailed(true);
          if (src && !reportedFailures.has(src)) {
            reportedFailures.add(src);
            addBreadcrumb("Atmospheric image failed to load", {
              category: "asset",
              level: "warning",
              data: { src },
            });
            // Warning-severity, non-blocking. Fallback gradient is already
            // rendered, so user experience is unaffected.
            captureError(new Error(`Atmospheric image failed to load: ${src}`), {
              severity: "warning",
              tags: { component: "AtmosphericBackdrop", asset_type: "atmospheric" },
              extra: { src, location: typeof window !== "undefined" ? window.location.pathname : undefined },
            });
          }
        }}
        className={className}
        style={{
          opacity: noFade || loaded ? undefined : 0,
          transition: noFade ? undefined : `opacity ${fadeMs}ms ease-out`,
        }}
      />
    </>
  );
};

export default AtmosphericBackdrop;
